import {
    $getNodeByKey,
    DecoratorNode,
    DOMExportOutput,
    EditorConfig,
    LexicalEditor,
    LexicalNode,
    NodeKey
} from "lexical";
import React, {JSX, useCallback, useRef} from "react";
import {useLexicalComposerContext} from "@lexical/react/LexicalComposerContext";

export type SerializedImageNode = {
    id?: string;
    src: string;
    altText?: string;
    type: "image";
    version: 1;
};

export class ImageNode extends DecoratorNode<JSX.Element> {
    __id?: string;
    __src: string;
    __altText?: string;

    static getType(): string {
        return "image";
    }

    static clone(node: ImageNode): ImageNode {
        return new ImageNode(node.__src, node.__altText, node.__id, node.__key);
    }

    constructor(src = "", altText = "", id?: string, key?: NodeKey) {
        super(key);
        this.__src = src;
        this.__altText = altText;
        this.__id = id;
    }

    static importJSON(serializedNode: SerializedImageNode): ImageNode {
        const {src, altText, id} = serializedNode;
        return new ImageNode(src, altText, id);
    }

    exportJSON(): SerializedImageNode {
        return {
            id: this.__id,
            src: this.__src,
            altText: this.__altText,
            type: "image",
            version: 1,
        };
    }

    createDOM(): HTMLElement {
        return document.createElement("span");
    }

    exportDOM(_editor: LexicalEditor): DOMExportOutput {
        const wrapper = document.createElement("div");
        wrapper.className = "image-node";

        const img = document.createElement("img");
        img.src = this.__src;
        img.alt = this.__altText || "";
        img.loading = "lazy";
        img.className = "rich-text-img";
        if (this.__id) img.setAttribute("data-id", this.__id);

        wrapper.appendChild(img);
        return { element: wrapper };
    }

    updateDOM(_prevNode: ImageNode, _dom: HTMLElement, _config: EditorConfig): boolean {
        return false;
    }

    decorate(editor: LexicalEditor, _config: EditorConfig): JSX.Element {
        return (
            <ImageComponent
                editor={editor}
                src={this.__src}
                altText={this.__altText}
                nodeKey={this.__key}
                id={this.__id}
            />
        );
    }
}

interface ImageComponentProps {
    editor: LexicalEditor;
    src: string;
    altText?: string;
    nodeKey: NodeKey;
    id?: string;
}

const ImageComponent: React.FC<ImageComponentProps> = (props) => {
    const {
        editor,
        src,
        altText,
        nodeKey,
        id,
    } = props;

    useLexicalComposerContext();

    const wrapperRef = useRef<HTMLDivElement | null>(null);

    const handleKeyDown = useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
        const {key} = event;

        if (key === "Delete" || key === "Backspace") {
            event.preventDefault();
            editor.update(() => {
                const node = $getNodeByKey(nodeKey) as LexicalNode | null;
                if (node) {
                    node.remove();
                }
            });
            return;
        }

        if (key === "ArrowLeft") {
            event.preventDefault();
            editor.update(() => {
                const node = $getNodeByKey(nodeKey) as ImageNode | null;
                if (node && typeof (node as any).selectPrevious === "function") {
                    (node as any).selectPrevious();
                }
            });
            return;
        }

        if (key === "ArrowRight") {
            event.preventDefault();
            editor.update(() => {
                const node = $getNodeByKey(nodeKey) as ImageNode | null;
                if (node && typeof (node as any).selectNext === "function") {
                    (node as any).selectNext();
                }
            });
        }
    }, [editor, nodeKey]);

    const handleImageLoad = useCallback((event: React.SyntheticEvent<HTMLImageElement>) => {
        const img = event.currentTarget;
        const wrapper = wrapperRef.current;
        if (wrapper) {
            wrapper.classList.remove("loading");
        }

        const editorElem = img.closest(".rich-text-editor") as HTMLElement | null;
        if (editorElem) {
            editorElem.style.height = "auto";
            editorElem.style.height = editorElem.scrollHeight + "px";
        }
    }, []);

    const handleImageError = useCallback(() => {
        const wrapper = wrapperRef.current;
        if (wrapper) {
            wrapper.classList.remove("loading");
            wrapper.classList.add("error");
        }
    }, []);

    const handleClick = useCallback(() => {
        if (wrapperRef.current) {
            wrapperRef.current.focus();
        }
    }, []);

    return (
        <div
            ref={wrapperRef}
            className="image-node loading outline-none focus:ring-2 focus:ring-primary/40"
            tabIndex={0}
            onKeyDown={handleKeyDown}
            onClick={handleClick}
        >
            <img
                src={src}
                alt={altText || ""}
                loading="lazy"
                contentEditable={false}
                data-id={id}
                onLoad={handleImageLoad}
                onError={handleImageError}
            />
        </div>
    );
};

export function $createImageNode({src, altText, id,}: {
    src: string;
    altText?: string;
    id?: string;
}): ImageNode {
    return new ImageNode(src, altText, id);
}

export function $isImageNode(node: unknown): node is ImageNode {
    return node instanceof ImageNode;
}
